import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getAllPayments, updatePaymentStatus } from "@/services/paymentService";
import type { Payment, FirestoreDate } from "@/types/payment";
import { PaymentStatus, PaymentGatewayType } from "@/types/payment";
import { queryKeys } from "@/lib/queryKeys";
import {
  Search,
  CreditCard,
  DollarSign,
  Clock,
  AlertTriangle,
  Eye,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

function formatFirestoreDate(value: FirestoreDate | null | undefined): string {
  if (value == null) return "\u2014";
  const d =
    value instanceof Date
      ? value
      : "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function"
        ? (value as { toDate: () => Date }).toDate()
        : "seconds" in value
          ? new Date((value as { seconds: number }).seconds * 1000)
          : null;
  return d
    ? d.toLocaleDateString("es-DO", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "\u2014";
}

function formatCurrency(amount: number): string {
  return `RD$ ${amount.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_LABELS: Record<string, string> = {
  [PaymentStatus.PENDING]: "Pendiente",
  [PaymentStatus.PROCESSING]: "Procesando",
  [PaymentStatus.COMPLETED]: "Completado",
  [PaymentStatus.FAILED]: "Fallido",
  [PaymentStatus.CANCELLED]: "Cancelado",
  [PaymentStatus.REFUNDED]: "Reembolsado",
  [PaymentStatus.MANUAL_VERIFICATION]: "Por Verificar",
};

const STATUS_COLORS: Record<string, string> = {
  [PaymentStatus.PENDING]: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200",
  [PaymentStatus.PROCESSING]: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200",
  [PaymentStatus.COMPLETED]: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200",
  [PaymentStatus.FAILED]: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200",
  [PaymentStatus.CANCELLED]: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200",
  [PaymentStatus.REFUNDED]: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200",
  [PaymentStatus.MANUAL_VERIFICATION]: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200",
};

const GATEWAY_LABELS: Record<string, string> = {
  [PaymentGatewayType.CARDNET]: "CARDNET",
  [PaymentGatewayType.AZUL]: "AZUL",
  [PaymentGatewayType.TPAGO]: "TPAGO",
  [PaymentGatewayType.PAYPAL]: "PayPal",
  [PaymentGatewayType.MANUAL_BANK]: "Transferencia",
  [PaymentGatewayType.CASH]: "Efectivo",
  [PaymentGatewayType.STRIPE]: "Stripe",
};

type TabFilter = "all" | "pending" | "completed" | "failed" | "manual_verification";

const TAB_STATUS_MAP: Record<TabFilter, PaymentStatus | null> = {
  all: null,
  pending: PaymentStatus.PENDING,
  completed: PaymentStatus.COMPLETED,
  failed: PaymentStatus.FAILED,
  manual_verification: PaymentStatus.MANUAL_VERIFICATION,
};

export function Payments() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: payments = [], isLoading } = useQuery({
    queryKey: queryKeys.payments,
    queryFn: getAllPayments as () => Promise<Payment[]>,
  });

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<TabFilter>("all");
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const filtered = payments.filter((p) => {
    const statusFilter = TAB_STATUS_MAP[activeTab];
    if (statusFilter && p.status !== statusFilter) return false;

    if (search) {
      const term = search.toLowerCase();
      const name = (p.userName ?? "").toLowerCase();
      const email = (p.userEmail ?? "").toLowerCase();
      const ref = (p.reference ?? "").toLowerCase();
      const txId = (p.transactionId ?? "").toLowerCase();
      return (
        name.includes(term) ||
        email.includes(term) ||
        ref.includes(term) ||
        txId.includes(term)
      );
    }
    return true;
  });

  // Stats
  const totalCompleted = payments
    .filter((p) => p.status === PaymentStatus.COMPLETED)
    .reduce((sum, p) => sum + p.amount, 0);
  const totalPending = payments
    .filter((p) => p.status === PaymentStatus.PENDING)
    .reduce((sum, p) => sum + p.amount, 0);
  const now = new Date();
  const monthTransactions = payments.filter((p) => {
    const d =
      p.createdAt instanceof Date
        ? p.createdAt
        : "seconds" in p.createdAt
          ? new Date((p.createdAt as { seconds: number }).seconds * 1000)
          : null;
    return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const manualVerificationCount = payments.filter(
    (p) => p.status === PaymentStatus.MANUAL_VERIFICATION
  ).length;

  async function handleVerify(payment: Payment) {
    setActionLoading(true);
    try {
      await updatePaymentStatus(payment.id, PaymentStatus.COMPLETED, "Verificado manualmente");
      toast.success("Pago verificado exitosamente");
      queryClient.invalidateQueries({ queryKey: queryKeys.payments });
      setSelectedPayment(null);
    } catch {
      toast.error("Error al verificar el pago");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject(payment: Payment) {
    setActionLoading(true);
    try {
      await updatePaymentStatus(payment.id, PaymentStatus.FAILED, "Rechazado manualmente");
      toast.success("Pago rechazado");
      queryClient.invalidateQueries({ queryKey: queryKeys.payments });
      setSelectedPayment(null);
    } catch {
      toast.error("Error al rechazar el pago");
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Pagos</h1>
        <p className="text-muted-foreground">
          Gestión y seguimiento de todos los pagos
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/30">
              <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Cobrado</p>
              <p className="text-xl font-semibold">{formatCurrency(totalCompleted)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="rounded-lg bg-yellow-100 p-2 dark:bg-yellow-900/30">
              <Clock className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pendiente</p>
              <p className="text-xl font-semibold">{formatCurrency(totalPending)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/30">
              <CreditCard className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Transacciones del Mes</p>
              <p className="text-xl font-semibold">{monthTransactions}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 pt-6">
            <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/30">
              <AlertTriangle className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Por Verificar</p>
              <p className="text-xl font-semibold">{manualVerificationCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabFilter)}>
        <TabsList>
          <TabsTrigger value="all">Todos</TabsTrigger>
          <TabsTrigger value="pending">Pendientes</TabsTrigger>
          <TabsTrigger value="completed">Completados</TabsTrigger>
          <TabsTrigger value="failed">Fallidos</TabsTrigger>
          <TabsTrigger value="manual_verification">Por Verificar</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, email, referencia o ID de transacción..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
            />
          </div>

          {isLoading ? (
            <p className="text-muted-foreground">Cargando pagos...</p>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
              <CreditCard className="h-10 w-10 opacity-50" />
              <p>
                {payments.length === 0
                  ? "No hay pagos registrados."
                  : "Ningún pago coincide con los filtros."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Método de Pago</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Referencia</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((p) => (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/payments/${p.id}`)}>
                      <TableCell className="whitespace-nowrap text-muted-foreground">
                        {formatFirestoreDate(p.paymentDate ?? p.createdAt)}
                      </TableCell>
                      <TableCell>
                        <span className="block font-medium">{p.userName ?? "\u2014"}</span>
                        {p.userEmail && (
                          <span className="block text-xs text-muted-foreground">
                            {p.userEmail}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(p.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {GATEWAY_LABELS[p.gatewayType] ?? p.gatewayType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[p.status] ?? ""}`}
                        >
                          {STATUS_LABELS[p.status] ?? p.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {p.reference ?? p.transactionId ?? "\u2014"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Link to={`/payments/${p.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {p.status === PaymentStatus.MANUAL_VERIFICATION && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleVerify(p)}
                                disabled={actionLoading}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleReject(p)}
                                disabled={actionLoading}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Detail Dialog */}
      <Dialog open={!!selectedPayment} onOpenChange={(open) => !open && setSelectedPayment(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle del Pago</DialogTitle>
            <DialogDescription>
              Información completa de la transacción
            </DialogDescription>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">ID</p>
                  <p className="font-mono text-xs">{selectedPayment.id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Estado</p>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[selectedPayment.status] ?? ""}`}
                  >
                    {STATUS_LABELS[selectedPayment.status] ?? selectedPayment.status}
                  </span>
                </div>
                <div>
                  <p className="text-muted-foreground">Usuario</p>
                  <p className="font-medium">{selectedPayment.userName ?? "\u2014"}</p>
                  {selectedPayment.userEmail && (
                    <p className="text-xs text-muted-foreground">{selectedPayment.userEmail}</p>
                  )}
                </div>
                <div>
                  <p className="text-muted-foreground">Monto</p>
                  <p className="font-medium">{formatCurrency(selectedPayment.amount)}</p>
                  {selectedPayment.currency && selectedPayment.currency !== "DOP" && (
                    <p className="text-xs text-muted-foreground">{selectedPayment.currency}</p>
                  )}
                </div>
                <div>
                  <p className="text-muted-foreground">Método</p>
                  <p>{GATEWAY_LABELS[selectedPayment.gatewayType] ?? selectedPayment.gatewayType}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Fecha</p>
                  <p>{formatFirestoreDate(selectedPayment.paymentDate ?? selectedPayment.createdAt)}</p>
                </div>
                {selectedPayment.transactionId && (
                  <div>
                    <p className="text-muted-foreground">ID Transacción</p>
                    <p className="font-mono text-xs">{selectedPayment.transactionId}</p>
                  </div>
                )}
                {selectedPayment.reference && (
                  <div>
                    <p className="text-muted-foreground">Referencia</p>
                    <p className="font-mono text-xs">{selectedPayment.reference}</p>
                  </div>
                )}
                {selectedPayment.orderId && (
                  <div>
                    <p className="text-muted-foreground">Orden</p>
                    <p className="font-mono text-xs">{selectedPayment.orderId}</p>
                  </div>
                )}
                {selectedPayment.financingRequestId && (
                  <div>
                    <p className="text-muted-foreground">Solicitud Financiamiento</p>
                    <p className="font-mono text-xs">{selectedPayment.financingRequestId}</p>
                  </div>
                )}
                {selectedPayment.processingFee != null && (
                  <div>
                    <p className="text-muted-foreground">Comisión</p>
                    <p>{formatCurrency(selectedPayment.processingFee)}</p>
                  </div>
                )}
                {selectedPayment.netAmount != null && (
                  <div>
                    <p className="text-muted-foreground">Monto Neto</p>
                    <p>{formatCurrency(selectedPayment.netAmount)}</p>
                  </div>
                )}
                {selectedPayment.notes && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Notas</p>
                    <p className="whitespace-pre-wrap text-sm">{selectedPayment.notes}</p>
                  </div>
                )}
              </div>

              {selectedPayment.voucherUrl && (
                <div>
                  <p className="mb-2 text-sm text-muted-foreground">Comprobante</p>
                  <img
                    src={selectedPayment.voucherUrl}
                    alt="Comprobante de pago"
                    className="max-h-64 rounded-lg border object-contain"
                  />
                </div>
              )}

              {selectedPayment.status === PaymentStatus.MANUAL_VERIFICATION && (
                <div className="flex justify-end gap-2 border-t pt-4">
                  <Button
                    variant="destructive"
                    onClick={() => handleReject(selectedPayment)}
                    disabled={actionLoading}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Rechazar
                  </Button>
                  <Button
                    onClick={() => handleVerify(selectedPayment)}
                    disabled={actionLoading}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Verificar
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
